import React, {useEffect, useMemo, useState} from 'react';
import {FormLayout} from './FormLayout';
import {Button, OtpInput, Text} from '@/ui-kits';
import {TestIDKeys, resetOtpTimer} from '@/constants';
import {OTPValidation} from '@/utils/schema';
import {useFormik} from 'formik';
import {
  maskEmail,
  appendPlural,
  cn,
  maskPhone,
} from '@/utils/common_functions';
import {
  loginOTPVerifyRequest,
  loginMFAVerifyRequest,
  singupOTPVerifyRequest,
  signupMFAVerifyRequest,
  loginOtpResendRequest,
  loginMFAResendRequest,
  signupOtpResendRequest,
  signupMFAResendRequest,
} from '@/services/redux/slices';
import {useDispatch, useSelector} from 'react-redux';
import {
  otpAttemptsSelector,
  otpTimerSelector,
} from '@/services/redux/selectors';
import {useSecondsTimer, useFormSubmit} from '@/hooks';

/**
 * otp Form interface types
 */
type OTPFormType = 'MFA' | 'OTP';
interface OTPFormProps {
  email: string;
  phone?: string;
  isSignup?: boolean;
  variant?: OTPFormType;
  handlePrevious?: () => void;
  disabled: boolean;
}

interface OTPFormValue {
  otp: string;
}
const initialFormValue: OTPFormValue = {otp: ''};

export const OTPForm: React.FC<OTPFormProps> = props => {
  const {
    isSignup = false,
    email,
    handlePrevious,
    variant = 'OTP',
    disabled,
    phone,
  } = props;

  // ================================
  // hooks
  // ================================
  const dispatch = useDispatch();
  const {
    values,
    dirty,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    setFieldValue,
  } = useFormik({
    initialValues: initialFormValue,
    validationSchema: OTPValidation,
    onSubmit,
  });


  // ================================
  // selectors
  // ================================
  const attempts = useSelector(otpAttemptsSelector);
  const otpLastUpdate = useSelector(otpTimerSelector);  
  
  // ================================
  // states
  // ================================
  const remainingTime = useSecondsTimer({
    refferenceTimeStamp: otpLastUpdate,
    timerLimit: resetOtpTimer,
  });
  const canResendOTP = useMemo<boolean>(
    () => Boolean(Number(remainingTime) <= 0),
    [remainingTime],
  );

  // ================================
  // functions
  // ================================
  function resendOTP() {
    if (!canResendOTP) {
      return;
    }
    const payload = {email_id: email};
    if (variant === 'MFA') {
      if (isSignup) {
        dispatch(signupMFAResendRequest(payload));
      } else {
        dispatch(loginMFAResendRequest(payload));
      }
    } else {
      if (isSignup) {
        dispatch(signupOtpResendRequest(payload));
      } else {
        dispatch(loginOtpResendRequest(payload));
      }
    }
    // reset the otp upon resend
    setFieldValue('otp', '');
  }
  /**
   * Handle form submission
   */
  function onSubmit(value: OTPFormValue) {
    const payload = {
      email_id: email,
      otp: value.otp,
    };
    const mfa_payload = {
      email_id: email,
      mfa: value.otp,
    };

    if (variant === 'MFA') {
      if (isSignup) {
        dispatch(signupMFAVerifyRequest(mfa_payload));
      } else {
        dispatch(loginMFAVerifyRequest(mfa_payload));
      }
    } else {
      if (isSignup) {
        dispatch(singupOTPVerifyRequest(payload));
      } else {
        dispatch(loginOTPVerifyRequest(payload));
      }
    }
  }

  return (
    <FormLayout wrapperClassName="pt-0">
      <div className="flex flex-col grow items-center gap-2">
        {/* header */}
        <Text
          className="font-headingBold text-ui_blue my-4 text-center text-4xl whitespace-pre-line"
          variant="heading">
          {'Multi Factor\nAuthentication'}
        </Text>

        <Text className="font-Label text-grey text-center" variant="body">
          {variant === 'MFA'
            ? `Enter the OTP sent to ${phone}`
            : `Enter the OTP sent to ${maskEmail(email)}`}
          {' '}
        </Text>

        {/* otp input */}
        <div className="flex flex-col mt-10">
          <OtpInput
            value={values.otp}
            onChange={handleChange('otp')}
            onBlur={handleBlur('otp')}
            error={errors.otp && ' '}
            touched={touched.otp}
          />

          <div className="flex flex-col items-center mt-2">
            <Text className="font-FigtreeLight text-ui_blue mt-4">{`${attempts} ${appendPlural(
              'Attempt',
              attempts,
            )} Remaining`}</Text>

            {/* error text */}
            <div className="justify-center h-20">
              {touched.otp && errors.otp && (
                <Text className="my-3 text-rejection font-Label text-sm">
                  {errors.otp}
                </Text>
              )}
            </div>

            <OTPResendButton
              canResendOTP={canResendOTP}
              remainingTime={remainingTime}
              resendOTP={resendOTP}
            />
          </div>
        </div>

        {/* submit button */}
        <div className="flex gap-4 md:gap-6 lg:gap-8">
          <Button
            onClick={handlePrevious}
            className="w-full grow items-center self-center px-10 py-2 border-[1px] mb-6"
            title="Previous"
            variant="secondary"
          />

          <Button
            className="w-full grow items-center self-center px-10 py-2 border-[1px] mb-6"
            disabled={disabled || !isValid || !dirty}
            title="Verify"
            variant="primary"
            onClick={() => handleSubmit()}
            rightIcon="arrow_circle_white_right"
          />
        </div>

        <div className="flex flex-col items-center">
          {variant === 'MFA' ? (
            <Text className="max-w-400 text-center font-Body text-ui_blue text-sm inline">
              <span className="font-FigtreeSemiBold mr-1">
                Why this extra step?
              </span>
              For your security, we require multi-factor authentication to
              protect your health information. This ensures that only you can
              access your account.
            </Text>
          ) : null}
        </div>
      </div>
    </FormLayout>
  );
};

interface OTPResendButtonProps {
  canResendOTP: boolean;
  remainingTime: number;
  resendOTP: () => void;
}
export const OTPResendButton: React.FC<OTPResendButtonProps> = props => {
  const {canResendOTP, remainingTime, resendOTP} = props;

  const resendButtonText = canResendOTP
    ? 'Resend OTP'
    : `Resend OTP in ${remainingTime} ${appendPlural('second', remainingTime)}`;
  return (
    <div className="flex items-center gap-2">
      {canResendOTP && (
        <Text className="font-Label text-ui_blue text-sm self-end cursor-pointer opacity-80">
          Didn't Receive OTP?
        </Text>
      )}
      <button onClick={() => resendOTP()}>
        <Text
          className={cn(
            'font-Label underline text-ui_blue text-sm self-end cursor-pointer',
            !canResendOTP && 'text-grey',
          )}>
          {resendButtonText}
        </Text>
      </button>
    </div>
  );
};
